// Keeps the visitor's unsubmitted input on the server, so a reload or the passed-on link resumes there: shortly
// after the last change, at once when a step is taken, and when the page goes to the background.

export interface Autosave {
  /** Something changed: save shortly. */
  (): void;
  /** A step was taken: save now. */
  now(): void;
}

export function autosave<T>(root: HTMLElement, snapshot: () => T, send: (state: T) => Promise<unknown>, delay = 600): Autosave {
  let timer = 0;
  let busy = false;
  // What the server holds. The stage starts from it, and an unchanged state is never sent again.
  let stored = JSON.stringify(snapshot());
  const flush = async (): Promise<void> => {
    window.clearTimeout(timer);
    if (busy) return;
    busy = true;
    try {
      // One request at a time, and the state is read when a request leaves: the newest state always goes last.
      for (;;) {
        const state = snapshot();
        const next = JSON.stringify(state);
        if (next === stored) break;
        await send(state);
        stored = next;
      }
    } catch {
      // A draft is a convenience: the next change tries again.
    }
    busy = false;
  };
  const onHide = () => {
    if (!root.isConnected) return document.removeEventListener('visibilitychange', onHide);
    // A phone may drop the tab in the background; what is typed is on the server before that.
    if (document.visibilityState === 'hidden') void flush();
  };
  document.addEventListener('visibilitychange', onHide);
  const save = (() => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void flush(), delay);
  }) as Autosave;
  save.now = () => void flush();
  return save;
}
